// MovimientoPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  People,
  Business,
  LocationOn,
  Home,
  Groups,
  AccountBalance,
  EmojiPeople
} from '@mui/icons-material';

// Colores de Alianza Social
const alianzaColors = {
  primary: '#1E88E5',
  secondary: '#E63946',
  yellow: '#FFC107',
  orange: '#FF9800',
  green: '#4CAF50',
  purple: '#9C27B0',
  darkGrey: '#424242',
  lightGrey: '#BDBDBD'
};

const StructureCard = ({ title, desc, icon: Icon, color, items }) => (
  <Card sx={{ 
    borderLeft: `6px solid ${color}`, 
    mb: 3,
    transition: 'all 0.3s',
    '&:hover': {
      transform: 'translateX(8px)',
      boxShadow: 6
    }
  }}>
    <CardContent sx={{ bgcolor: `${color}15`, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          bgcolor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon sx={{ fontSize: 28, color: 'white' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: color }}>
          {title}
        </Typography>
      </Box>
      
      {desc && (
        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
          {desc}
        </Typography>
      )}
      
      {items && (
        <Box component="ul" sx={{ pl: 2, mt: 2 }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '8px' }}>
              <Typography variant="body2">{item}</Typography>
            </li>
          ))}
        </Box>
      )}
    </CardContent>
  </Card>
);

const RoleCard = ({ title, description, location, color }) => (
  <Card sx={{ 
    height: '100%',
    borderTop: `4px solid ${color}`,
    transition: 'all 0.3s',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: 8
    }
  }}>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: color, mb: 2 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {description}
      </Typography>
      {location && (
        <Paper sx={{ p: 2, bgcolor: `${color}10`, borderRadius: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: color }}>
            📍 Ubicación:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            {location.map((loc, idx) => (
              <li key={idx}>
                <Typography variant="caption">{loc}</Typography>
              </li>
            ))}
          </Box>
        </Paper>
      )}
    </CardContent>
  </Card>
);

const MovimientoPage = () => {
  const navigate = useNavigate();

  const estructuraOrganizacional = [
    {
      title: 'Asamblea General de Miembros',
      desc: 'Máximo órgano de decisión democrática del movimiento, compuesto por todos los afiliados o sus delegados.',
      icon: Groups,
      color: alianzaColors.secondary,
      items: [
        'Aprobar y modificar estatutos',
        'Elegir Directorio Nacional',
        'Definir línea ideológica y programática',
        'Tomar decisiones estratégicas fundamentales'
      ]
    },
    {
      title: 'Consejo Político Nacional',
      desc: 'Órgano de dirección política permanente entre asambleas.',
      icon: AccountBalance,
      color: alianzaColors.primary,
      items: [
        'Presidente o Coordinador Nacional',
        'Vicepresidente o Vocero Adjunto',
        'Representantes regionales elegidos',
        'Congresistas del movimiento'
      ]
    },
    {
      title: 'Dirección Ejecutiva',
      desc: 'Estructura operativa que implementa las decisiones del Consejo.',
      icon: Business,
      color: alianzaColors.green,
      items: [
        'Dirección de Estrategia y Campaña',
        'Dirección de Comunicaciones y Opinión Pública',
        'Dirección de Formación y Liderazgo',
        'Dirección de Finanzas y Transparencia',
        'Dirección de Programas Sociales'
      ]
    },
    {
      title: 'Comités Temáticos',
      desc: 'Grupos especializados en áreas específicas del trabajo político.',
      icon: People,
      color: alianzaColors.purple,
      items: [
        'Comité de Juventud y Nuevos Liderazgos',
        'Comité de Mujeres y Equidad de Género',
        'Comité Ambiental y Sostenibilidad (ODS)',
        'Comité de Economía Solidaria y Colaborativa',
        'Comité de Participación Ciudadana'
      ]
    },
    {
      title: 'Bases Territoriales',
      desc: 'Estructuras organizadas por nivel geográfico.',
      icon: LocationOn,
      color: alianzaColors.orange,
      items: [
        'Coordinadores Nacionales',
        'Directorios Departamentales',
        'Directorios Municipales',
        'Comités de Base (Barrios y Veredas)',
        'Líderes comunitarios y voluntarios'
      ]
    }
  ];

  const nivelesTerritoriales = [
    {
      nivel: 'NACIONAL',
      color: alianzaColors.secondary,
      organos: [
        {
          nombre: 'Asamblea Nacional',
          composicion: 'Delegados de todo el país, directivos nacionales, congresistas electos',
          funciones: 'Máximo órgano de decisión, aprueba estatutos y elige directorio'
        },
        {
          nombre: 'Directorio Nacional',
          composicion: 'Presidente, Secretario General, Tesorero, Vocales (5-15 personas)',
          funciones: 'Dirección ejecutiva permanente, decisiones estratégicas'
        }
      ]
    },
    {
      nivel: 'DEPARTAMENTAL',
      color: alianzaColors.primary,
      organos: [
        {
          nombre: 'Asamblea Departamental',
          composicion: 'Delegados municipales, directivos departamentales, diputados electos',
          funciones: 'Elegir Directorio Departamental, adaptar estrategias regionales'
        },
        {
          nombre: 'Directorio Departamental',
          composicion: 'Coordinador Departamental, Secretario, Tesorero, Vocales',
          funciones: 'Coordinar municipios, implementar estrategias nacionales'
        }
      ]
    },
    {
      nivel: 'MUNICIPAL',
      color: alianzaColors.green,
      organos: [
        {
          nombre: 'Asamblea Municipal',
          composicion: 'Afiliados del municipio, delegados de comités, concejales electos',
          funciones: 'Elegir Directorio Municipal, avalar candidatos locales'
        },
        {
          nombre: 'Directorio Municipal',
          composicion: 'Coordinador Municipal, coordinadores de comuna/corregimiento',
          funciones: 'Organizar municipio, captar afiliados, campañas locales'
        }
      ]
    },
    {
      nivel: 'LOCAL (Comités de Base)',
      color: alianzaColors.purple,
      organos: [
        {
          nombre: 'Comités de Base',
          composicion: 'Coordinador de comité, afiliados, militantes activos, simpatizantes',
          funciones: 'Trabajo territorial directo, movilización electoral, trabajo comunitario'
        }
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 10 }}>
      <Container maxWidth="lg">
        {/* Botón volver */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ 
            mb: 4,
            fontWeight: 600,
            color: alianzaColors.secondary,
            '&:hover': {
              bgcolor: `${alianzaColors.secondary}15`
            }
          }}
        >
          Volver al inicio
        </Button>

        {/* Header */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 6, 
            mb: 6, 
            background: `linear-gradient(135deg, ${alianzaColors.primary} 0%, ${alianzaColors.green} 100%)`,
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 'bold', 
              color: 'white',
              mb: 2,
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            🏛️ Estructura del Movimiento
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'white',
              opacity: 0.95,
              maxWidth: 800,
              mx: 'auto'
            }}
          >
            Alianza Social está organizada democráticamente en múltiples niveles para garantizar participación efectiva
          </Typography>
        </Paper>

        {/* Introducción */}
        <Paper elevation={2} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: alianzaColors.darkGrey }}>
            Modelo Organizacional
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Alianza Social implementa un <strong>modelo mixto</strong> que combina la democracia participativa 
            con estructuras operativas eficientes. Nuestro diseño organizacional garantiza que:
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {[
              'Cada miembro tiene voz y voto en las decisiones',
              'Existe representación territorial en todos los niveles',
              'La toma de decisiones es transparente y democrática',
              'Se fomenta el liderazgo desde las bases'
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Typography sx={{ color: alianzaColors.green, fontWeight: 'bold' }}>✓</Typography>
                  <Typography variant="body2">{item}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Estructura Organizacional */}
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, color: alianzaColors.darkGrey }}>
          Órganos Principales
        </Typography>
        
        {estructuraOrganizacional.map((estructura, idx) => (
          <StructureCard key={idx} {...estructura} />
        ))}

        {/* Niveles Territoriales */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, color: alianzaColors.darkGrey }}>
            Estructura por Niveles Territoriales
          </Typography>
          
          {nivelesTerritoriales.map((nivel, idx) => (
            <Accordion 
              key={idx}
              sx={{ 
                mb: 2,
                borderLeft: `6px solid ${nivel.color}`,
                '&:before': { display: 'none' },
                boxShadow: 2
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: `${nivel.color}15`,
                  '&:hover': {
                    bgcolor: `${nivel.color}25`
                  }
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: nivel.color }}>
                  {nivel.nivel}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {nivel.organos.map((organo, oIdx) => (
                  <Paper key={oIdx} sx={{ p: 3, mb: oIdx < nivel.organos.length - 1 ? 3 : 0, bgcolor: '#FAFAFA' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: nivel.color }}>
                      {organo.nombre}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        Composición:
                      </Typography>
                      <Typography variant="body2">{organo.composicion}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        Funciones:
                      </Typography>
                      <Typography variant="body2">{organo.funciones}</Typography>
                    </Box>
                  </Paper>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Roles en la Estructura */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, color: alianzaColors.darkGrey }}>
            Integración de Roles
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <RoleCard
                title="👥 AFILIADOS"
                description="Cuerpo electoral interno que da legitimidad al movimiento"
                location={[
                  'Nivel Nacional: Delegados a Asamblea Nacional',
                  'Nivel Departamental: Participan en Asamblea Departamental',
                  'Nivel Municipal: Base de Asamblea Municipal',
                  'Nivel Local: Integran Comités de Base'
                ]}
                color={alianzaColors.purple}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RoleCard
                title="🚀 MILITANTES ACTIVOS"
                description="Motor operativo que ejecuta decisiones y moviliza afiliados"
                location={[
                  'Nivel Nacional: Cargos técnicos y de comunicación',
                  'Nivel Departamental: Coordinan áreas específicas',
                  'Nivel Municipal: Lideran comités temáticos',
                  'Nivel Local: Coordinadores de comités de base'
                ]}
                color={alianzaColors.green}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RoleCard
                title="⭐ DIRECTIVOS"
                description="Responsables de decisiones estratégicas y administración"
                location={[
                  'Nivel Nacional: Directorio Nacional',
                  'Nivel Departamental: Directorios Departamentales',
                  'Nivel Municipal: Directorios Municipales'
                ]}
                color={alianzaColors.primary}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RoleCard
                title="🏆 CANDIDATOS"
                description="Representan al movimiento en elecciones populares"
                location={[
                  'Presidencia/Congreso → Aval Directorio Nacional',
                  'Gobernación/Asamblea → Aval Directorio Departamental',
                  'Alcaldía/Concejo → Aval Directorio Municipal'
                ]}
                color={alianzaColors.orange}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Flujo de Autoridad */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 5, 
            mb: 6,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${alianzaColors.lightGrey}30 0%, white 100%)`
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center', color: alianzaColors.darkGrey }}>
            📊 Flujo de Autoridad y Comunicación
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {[
              { text: 'ASAMBLEA NACIONAL (soberana)', color: alianzaColors.secondary },
              { text: 'DIRECTORIO NACIONAL (ejecuta)', color: '#C62828' },
              { text: 'DIRECTORIOS DEPARTAMENTALES', color: alianzaColors.primary },
              { text: 'DIRECTORIOS MUNICIPALES', color: alianzaColors.green },
              { text: 'COMITÉS DE BASE', color: alianzaColors.purple },
              { text: 'CIUDADANÍA (votantes)', color: alianzaColors.darkGrey }
            ].map((nivel, idx) => (
              <React.Fragment key={idx}>
                <Paper
                  elevation={2}
                  sx={{
                    bgcolor: nivel.color,
                    color: 'white',
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: 500,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}
                >
                  {nivel.text}
                </Paper>
                {idx < 5 && (
                  <Typography sx={{ fontSize: '2rem', color: alianzaColors.lightGrey }}>
                    ↓
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center', 
              mt: 4, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            💡 La información también fluye de abajo hacia arriba, garantizando retroalimentación constante
          </Typography>
        </Paper>

        {/* Diferencias con Partidos Tradicionales */}
        <Paper elevation={3} sx={{ p: 5, mb: 6, borderRadius: 3, bgcolor: `${alianzaColors.yellow}15` }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4, color: alianzaColors.darkGrey }}>
            🌟 Diferencias con Partidos Tradicionales
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#FFEBEE', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#C62828' }}>
                  ❌ Partido Tradicional
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <li><Typography variant="body2">Jerarquía vertical rígida</Typography></li>
                  <li><Typography variant="body2">Cúpulas centralizadas</Typography></li>
                  <li><Typography variant="body2">Dependencia económica externa</Typography></li>
                  <li><Typography variant="body2">Enfoque electoral exclusivo</Typography></li>
                  <li><Typography variant="body2">Poca formación interna</Typography></li>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: '#E8F5E9', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: alianzaColors.green }}>
                  ✅ Alianza Social
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <li><Typography variant="body2">Participación horizontal</Typography></li>
                  <li><Typography variant="body2">Vocerías rotativas</Typography></li>
                  <li><Typography variant="body2">Autosostenibilidad solidaria</Typography></li>
                  <li><Typography variant="body2">Propósito transformador social</Typography></li>
                  <li><Typography variant="body2">Educación política permanente</Typography></li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action Final */}
        <Paper 
          elevation={5}
          sx={{ 
            p: 6, 
            background: `linear-gradient(135deg, ${alianzaColors.secondary} 0%, ${alianzaColors.orange} 50%, ${alianzaColors.yellow} 100%)`,
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white', mb: 3 }}>
            🤝 Sé Parte de Esta Estructura
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.95, mb: 4, maxWidth: 700, mx: 'auto' }}>
            Cada nivel necesita personas comprometidas. Tu participación construye democracia desde la base.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<EmojiPeople />}
              onClick={() => navigate('/desbloquear-niveles')}
              sx={{
                bgcolor: 'white',
                color: alianzaColors.secondary,
                fontWeight: 'bold',
                fontSize: '1.1rem',
                px: 4,
                py: 2,
                borderRadius: 3,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              Ver Niveles de Participación
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                px: 4,
                py: 2,
                borderRadius: 3,
                '&:hover': {
                  borderWidth: 2,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              Registrarme Ahora
            </Button>
          </Box>
        </Paper>

        {/* Footer informativo */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Para más información sobre cómo participar en cada nivel, contacta a tu coordinador territorial 
            o escríbenos a <strong>info@alianzasocial.org</strong>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default MovimientoPage;